import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency, formatDate } from "@/lib/utils";
import {
  ArrowLeft,
  Building2,
  Mail,
  Calendar,
  Users,
  FileText,
  Receipt,
  Wrench,
  MapPin,
  FolderOpen,
  Smartphone,
  Euro,
  Trophy,
  TicketCheck,
  Phone,
  Hash,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertCircle,
  Hammer,
  Monitor,
  Coins,
  Star,
  Package,
  ArrowLeftRight,
  ShieldCheck,
  ShieldAlert,
  Wallet,
  BellOff,
  BellRing,
  FileSpreadsheet,
  ExternalLink,
} from "lucide-react";

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" | "success" }> = {
  TRIAL: { label: "Essai", variant: "outline" },
  ACTIVE: { label: "Actif", variant: "success" },
  PAST_DUE: { label: "Retard paiement", variant: "destructive" },
  CANCELLED: { label: "Annulé", variant: "destructive" },
};

const PLAN_LABELS: Record<string, string> = {
  FREE: "Gratuit",
  STARTER: "Starter",
  PRO: "Pro",
  ENTERPRISE: "Enterprise",
};

const LIFECYCLE_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning" }> = {
  lead: { label: "Lead", variant: "outline" },
  trial: { label: "Essai", variant: "warning" },
  client: { label: "Client", variant: "success" },
  churned: { label: "Churned", variant: "destructive" },
};

function StatCard({
  label,
  value,
  icon: Icon,
  iconColor = "text-primary",
  sub,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  iconColor?: string;
  sub?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className={`h-4 w-4 ${iconColor}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function ProductStatusSection({ companyId }: { companyId: string }) {
  const { data: ps, isLoading } = trpc.admin.companies.getProductStatus.useQuery(
    { companyId } as any,
    { enabled: !!companyId }
  );

  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}><CardContent className="pt-6"><Skeleton className="h-20 w-full" /></CardContent></Card>
        ))}
      </div>
    );
  }

  if (!ps) return null;

  const products = (ps as any).products;

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {/* TravauxBTP */}
      <Card className={products.travauxbtp?.active ? "border-warning/30" : "opacity-60"}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Hammer className="h-4 w-4 text-warning" />
            TravauxBTP
          </CardTitle>
          <Badge variant={products.travauxbtp?.active ? "warning" : "secondary"}>
            {products.travauxbtp?.active ? "Actif" : "Inactif"}
          </Badge>
        </CardHeader>
        <CardContent>
          {products.travauxbtp?.active ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1"><Coins className="h-3.5 w-3.5" /> Crédits</span>
                <span className="font-medium">{products.travauxbtp.credits}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Leads total</span>
                <span className="font-medium">{products.travauxbtp.totalLeads}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Dépensé</span>
                <span className="font-medium">{formatCurrency(products.travauxbtp.totalSpent)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1"><Star className="h-3.5 w-3.5" /> Trust</span>
                <span className="font-medium">{products.travauxbtp.trustScore}%</span>
              </div>
              {products.travauxbtp.since && (
                <p className="text-xs text-muted-foreground pt-1">Depuis {formatDate(products.travauxbtp.since)}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Aucune activité TravauxBTP</p>
          )}
        </CardContent>
      </Card>

      {/* SaaS */}
      <Card className={products.saas?.active ? "border-primary/30" : "opacity-60"}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Monitor className="h-4 w-4 text-primary" />
            SaaS
          </CardTitle>
          <Badge variant={products.saas?.active ? "default" : "secondary"}>
            {products.saas?.active ? "Actif" : "Inactif"}
          </Badge>
        </CardHeader>
        <CardContent>
          {products.saas?.active ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-medium">{PLAN_LABELS[products.saas.plan] ?? products.saas.plan ?? "—"}</span>
              </div>
              {products.saas.since && (
                <p className="text-xs text-muted-foreground pt-1">Depuis {formatDate(products.saas.since)}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Aucun abonnement SaaS</p>
          )}
        </CardContent>
      </Card>

      {/* Mobile */}
      <Card className={products.mobile?.active ? "border-success/30" : "opacity-60"}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-success" />
            Mobile
          </CardTitle>
          <Badge variant={products.mobile?.active ? "success" : "secondary"}>
            {products.mobile?.active ? "Actif" : "Inactif"}
          </Badge>
        </CardHeader>
        <CardContent>
          {products.mobile?.active ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Utilisateurs connectés</span>
                <span className="font-medium">{products.mobile.connectedUsers}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Aucun utilisateur mobile</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Churn : bilan rentabilité moyen 90j (AOP-8511) ──────────────────────────
function ChurnProfitabilitySection({ companyId }: { companyId: string }) {
  const { data, isLoading } = trpc.admin.companies.profitability90d.useQuery(
    { id: companyId },
    { enabled: !!companyId },
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Bilan rentabilité moyen (90j)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data?.hasData) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Bilan rentabilité moyen (90j)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Aucun chantier clôturé sur les 90 derniers jours — pas de bilan de
            rentabilité disponible.
          </p>
        </CardContent>
      </Card>
    );
  }

  const marge = data.weightedMargePct ?? data.avgMargePct ?? 0;
  const inRed = data.structurallyInRed;
  const fmtPct = (v: number | null | undefined) =>
    v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)} %`;

  return (
    <Card className={inRed ? "border-destructive/40" : "border-success/30"}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />
          Bilan rentabilité moyen (90j)
        </CardTitle>
        {inRed ? (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" /> Structurellement dans le rouge
          </Badge>
        ) : (
          <Badge variant="success">Rentable</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {inRed && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
            Signal churn : marge structurellement négative sur 90j. Cible
            prioritaire pour le support & l'accompagnement (ou opportunité d'upsell
            ARTISAN→ENTERPRISE).
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              {marge < 0 ? (
                <TrendingDown className="h-3.5 w-3.5 text-destructive" />
              ) : (
                <TrendingUp className="h-3.5 w-3.5 text-success" />
              )}
              Marge moyenne (pondérée CA)
            </p>
            <p
              className={`text-lg font-semibold ${
                marge < 0 ? "text-destructive" : "text-success"
              }`}
            >
              {fmtPct(marge)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Marge moyenne (simple)</p>
            <p className="text-lg font-semibold">{fmtPct(data.avgMargePct)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Écart vs objectif</p>
            <p
              className={`text-lg font-semibold ${
                (data.avgEcartMargePct ?? 0) < 0 ? "text-destructive" : ""
              }`}
            >
              {fmtPct(data.avgEcartMargePct)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Chantiers clôturés</p>
            <p className="text-lg font-semibold">
              {data.siteCount}
              {data.sitesInRed > 0 && (
                <span className="text-xs font-normal text-destructive ml-1">
                  ({data.sitesInRed} dans le rouge)
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 text-sm border-t pt-3">
          <div className="flex justify-between sm:block">
            <span className="text-muted-foreground text-xs">CA réel cumulé</span>
            <p className="font-medium">{formatCurrency(data.totalRevenusReels)}</p>
          </div>
          <div className="flex justify-between sm:block">
            <span className="text-muted-foreground text-xs">Coûts réels cumulés</span>
            <p className="font-medium">{formatCurrency(data.totalCoutsReels)}</p>
          </div>
          <div className="flex justify-between sm:block">
            <span className="text-muted-foreground text-xs">Marge réelle cumulée</span>
            <p
              className={`font-medium ${
                data.totalMargeReelle < 0 ? "text-destructive" : ""
              }`}
            >
              {formatCurrency(data.totalMargeReelle)}
            </p>
          </div>
        </div>

        {data.worstSites.length > 0 && (
          <div className="border-t pt-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Chantiers les moins rentables
            </p>
            <div className="space-y-1.5">
              {data.worstSites.map((s) => (
                <div
                  key={s.siteId}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="truncate mr-2">{s.titre}</span>
                  <span
                    className={`font-medium tabular-nums ${
                      s.margePct < 0 ? "text-destructive" : "text-muted-foreground"
                    }`}
                  >
                    {s.margePct > 0 ? "+" : ""}
                    {s.margePct.toFixed(1)} %
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Adoption habilitations équipe (AOP-8579) ────────────────────────────────
function QualificationsAdoptionSection({ companyId }: { companyId: string }) {
  const { data, isLoading } = trpc.admin.companies.qualificationsAdoption.useQuery(
    { id: companyId },
    { enabled: !!companyId },
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Habilitations équipe
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const zero = data.zeroQualifications;
  const churnRisk = data.churnRisk;

  return (
    <Card className={churnRisk ? "border-destructive/40" : undefined}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Habilitations équipe
        </CardTitle>
        {zero ? (
          <Badge variant="destructive" className="flex items-center gap-1">
            <ShieldAlert className="h-3.5 w-3.5" /> 0 habilitation saisie
          </Badge>
        ) : (
          <Badge variant="success">{data.totalQualifications} saisie{data.totalQualifications > 1 ? "s" : ""}</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {churnRisk && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
            Risque churn : client ENTERPRISE sans aucune habilitation saisie. La
            feature RH/compliance n'est pas adoptée — cible prioritaire pour
            l'onboarding & l'accompagnement.
          </div>
        )}
        {zero && !churnRisk && (
          <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-xs text-warning-foreground">
            Aucune habilitation saisie — la feature RH/compliance n'est pas
            encore utilisée par ce client.
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs text-muted-foreground">Habilitations saisies</p>
            <p
              className={`text-lg font-semibold ${
                zero ? "text-destructive" : ""
              }`}
            >
              {data.totalQualifications}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Effectif équipe</p>
            <p className="text-lg font-semibold">{data.teamSize}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3.5 w-3.5" />
              Membres sans habilitation
            </p>
            <p
              className={`text-lg font-semibold ${
                data.membersWithoutQualif > 0 ? "text-warning-foreground" : ""
              }`}
            >
              {data.membersWithoutQualif}
              {data.teamSize > 0 && (
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  / {data.teamSize}
                </span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Habilitations expirées</p>
            <p
              className={`text-lg font-semibold ${
                data.expiredCount > 0 ? "text-destructive" : ""
              }`}
            >
              {data.expiredCount}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Suivi budgétaire chantiers + désactiver alertes (AOP-8587 / AOP-8456) ────
function BudgetTrackingSection({ companyId }: { companyId: string }) {
  const { user } = useAuth();
  const canAct = user?.staffRole === "SUPER_ADMIN" || user?.staffRole === "ADMIN_OPS";
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.admin.companies.getSitesBudgetTracking.useQuery(
    { companyId },
    { enabled: !!companyId && canAct },
  );

  const reset = trpc.admin.companies.resetSiteBudgetAlert.useMutation({
    onSuccess: () => {
      toast.success("Alerte budget désactivée");
      utils.admin.companies.getSitesBudgetTracking.invalidate({ companyId });
    },
    onError: (err) => toast.error(err.message),
  });

  // Réservé ADMIN_OPS + SUPER_ADMIN (la query échouerait pour ADMIN_SUPPORT).
  if (!canAct) return null;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Wallet className="h-4 w-4 text-primary" />
            Suivi budgétaire chantiers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Wallet className="h-4 w-4 text-primary" />
            Suivi budgétaire chantiers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Aucun chantier pour cette entreprise.</p>
        </CardContent>
      </Card>
    );
  }

  const activeAlerts = data.filter((s) => s.lastBudgetAlertLevel != null).length;

  // Couleur du pourcentage consommé : vert ≤80, orange ≤100, rouge >100.
  const pctClass = (s: (typeof data)[number]) => {
    if (!s.budgetConfigured) return "text-muted-foreground";
    if (s.alertLevel === "critical") return "text-destructive";
    if (s.alertLevel === "warning") return "text-warning-foreground";
    return "text-emerald-600";
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Wallet className="h-4 w-4 text-primary" />
          Suivi budgétaire chantiers
        </CardTitle>
        {activeAlerts > 0 && (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" /> {activeAlerts} alerte{activeAlerts > 1 ? "s" : ""} active{activeAlerts > 1 ? "s" : ""}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Diagnostic support : % budget consommé (dépenses TTC vs budget prévu) par
          chantier. Désactivez une alerte en cas de faux positif (budget mal configuré,
          HT/TTC).
        </p>
        <div className="space-y-2">
          {data.map((s) => (
            <div
              key={s.siteId}
              className="flex items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{s.title}</p>
                <p className="text-xs text-muted-foreground">
                  {s.budgetConfigured ? (
                    <>
                      {formatCurrency(s.totalDepenses)} dépensés / {formatCurrency(s.budgetPrevu)} budget
                    </>
                  ) : (
                    "Budget non configuré"
                  )}
                </p>
                {s.lastBudgetAlertLevel != null && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3" />
                    Alerte « {s.lastBudgetAlertLevel} » envoyée
                    {s.lastBudgetAlertSentAt ? ` le ${formatDate(s.lastBudgetAlertSentAt)}` : ""}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-lg font-semibold tabular-nums ${pctClass(s)}`}>
                  {s.budgetConfigured ? `${s.pctConsomme}%` : "—"}
                </span>
                {s.lastBudgetAlertLevel != null && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={reset.isPending && reset.variables?.siteId === s.siteId}
                    onClick={() => reset.mutate({ siteId: s.siteId })}
                  >
                    <BellOff className="mr-1 h-3.5 w-3.5" />
                    Désactiver l'alerte
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Facture récapitulative RTC par chantier (AOP-8954 / AOP-8626) ───────────
const INVOICE_STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" | "success" | "warning" }
> = {
  DRAFT: { label: "Brouillon", variant: "secondary" },
  SENT: { label: "Envoyée", variant: "default" },
  PAID: { label: "Payée", variant: "success" },
  LATE: { label: "En retard", variant: "destructive" },
  CANCELLED: { label: "Annulée", variant: "outline" },
};

// Niveaux d'escalade des relances facture (cf services/autoReminders.ts INVOICE_LEVEL_LABELS).
const REMINDER_LEVEL_LABELS: Record<string, string> = {
  REMINDER_1: "1ère relance",
  REMINDER_2: "2ème relance",
  REMINDER_3: "3ème relance",
  FORMAL_NOTICE: "Mise en demeure",
};

const REMINDER_CHANNEL_LABELS: Record<string, string> = {
  email: "Email",
  sms: "SMS",
  push: "Push",
};

function RecapInvoiceSection({ companyId }: { companyId: string }) {
  const { data, isLoading } = trpc.admin.companies.getSitesRecapStatus.useQuery(
    { companyId },
    { enabled: !!companyId },
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
            Factures récapitulatives (RTC)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Section purement informative : on ne l'affiche que si au moins une RTC existe.
  if (!data || data.length === 0) return null;

  return (
    <Card className="border-primary/30">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-primary" />
          Factures récapitulatives (RTC)
        </CardTitle>
        <Badge variant="success">
          {data.length} RTC générée{data.length > 1 ? "s" : ""}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Diagnostic support : chantiers pour lesquels une facture récapitulative
          (RTC, acomptes déduits) a été émise. Cliquez pour ouvrir la facture côté
          client.
        </p>
        <div className="space-y-2">
          {data.map((s: {
            siteId: string;
            title: string;
            recapGeneratedAt?: string | Date | null;
            invoice?: {
              status: string;
              number: string;
              totalTTC: number;
              url?: string | null;
            } | null;
          }) => {
            const statusCfg = s.invoice
              ? INVOICE_STATUS_CONFIG[s.invoice.status] ?? {
                  label: s.invoice.status,
                  variant: "secondary" as const,
                }
              : null;
            return (
              <div
                key={s.siteId}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{s.title}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <FileSpreadsheet className="h-3 w-3" />
                    RTC générée
                    {s.recapGeneratedAt ? ` le ${formatDate(s.recapGeneratedAt)}` : ""}
                    {s.invoice ? ` · n° ${s.invoice.number}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {s.invoice && (
                    <span className="text-sm font-semibold tabular-nums">
                      {formatCurrency(s.invoice.totalTTC)}
                    </span>
                  )}
                  {statusCfg && (
                    <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                  )}
                  {s.invoice?.url ? (
                    <Button asChild variant="outline" size="sm">
                      <a href={s.invoice.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-1 h-3.5 w-3.5" />
                        Voir la facture
                      </a>
                    </Button>
                  ) : s.invoice ? (
                    <Badge variant="outline">{s.invoice.number}</Badge>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Relances auto factures : statut + reset OPS (AOP-8956 / AOP-8632) ─────────
function ReminderScheduleSection({ companyId }: { companyId: string }) {
  const { user } = useAuth();
  const canAct = user?.staffRole === "SUPER_ADMIN" || user?.staffRole === "ADMIN_OPS";
  const utils = trpc.useUtils();

  const [pending, setPending] = useState<
    { scheduleId: string; action: "disable" | "reset"; label: string } | null
  >(null);

  const { data, isLoading } =
    trpc.admin.reminderSchedules.listInvoiceReminderSchedules.useQuery(
      { companyId },
      { enabled: !!companyId && canAct },
    );

  const remediate = trpc.admin.reminderSchedules.resetInvoiceReminderSchedule.useMutation({
    onSuccess: (res) => {
      toast.success(
        res.enabled
          ? `Relance réinitialisée au niveau 1${res.deletedReminders > 0 ? ` (${res.deletedReminders} relance(s) purgée(s))` : ""}`
          : "Relance automatique désactivée",
      );
      utils.admin.reminderSchedules.listInvoiceReminderSchedules.invalidate({ companyId });
      setPending(null);
    },
    onError: (err) => toast.error(err.message),
  });

  // Réservé ADMIN_OPS + SUPER_ADMIN (la query échouerait pour ADMIN_SUPPORT).
  if (!canAct) return null;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BellRing className="h-4 w-4 text-primary" />
            Relances automatiques factures
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  // Diagnostic support : masqué si aucune relance auto facture n'est configurée.
  if (!data || data.length === 0) return null;

  const activeCount = data.filter((s) => s.enabled).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <BellRing className="h-4 w-4 text-primary" />
          Relances automatiques factures
        </CardTitle>
        <Badge variant={activeCount > 0 ? "default" : "secondary"}>
          {activeCount} active{activeCount > 1 ? "s" : ""} / {data.length}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Support OPS : état des relances automatiques de factures impayées.
          « Désactiver » coupe une relance envoyée par erreur ; « Réinitialiser le
          niveau » relance le cycle au niveau 1 et purge l'historique d'escalade
          (action auditée).
        </p>
        <div className="space-y-2">
          {data.map((s) => {
            const statusCfg = s.invoice
              ? INVOICE_STATUS_CONFIG[s.invoice.status] ?? {
                  label: s.invoice.status,
                  variant: "secondary" as const,
                }
              : null;
            return (
              <div
                key={s.scheduleId}
                className="flex items-center justify-between gap-3 rounded-md border p-3"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="truncate text-sm font-medium">
                      {s.invoice
                        ? `Facture ${s.invoice.number}`
                        : "Facture supprimée (planning orphelin)"}
                    </p>
                    {s.invoice?.client?.name && (
                      <span className="truncate text-xs text-muted-foreground">
                        · {s.invoice.client.name}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span>Délai {s.delayDays} j</span>
                    <span>· {REMINDER_CHANNEL_LABELS[s.channel] ?? s.channel}</span>
                    {s.nextReminderAt && (
                      <span>· Prochaine : {formatDate(s.nextReminderAt)}</span>
                    )}
                    {s.lastReminder ? (
                      <span>
                        · Dernier niveau :{" "}
                        {REMINDER_LEVEL_LABELS[s.lastReminder.level] ??
                          s.lastReminder.level}{" "}
                        ({formatDate(s.lastReminder.sentAt)})
                      </span>
                    ) : (
                      <span>· Aucune relance encore envoyée</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {statusCfg && <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>}
                  <Badge variant={s.enabled ? "success" : "outline"}>
                    {s.enabled ? "Active" : "Désactivée"}
                  </Badge>
                  {s.enabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setPending({
                          scheduleId: s.scheduleId,
                          action: "disable",
                          label: s.invoice ? `la facture ${s.invoice.number}` : "cette facture",
                        })
                      }
                    >
                      <BellOff className="mr-1 h-3.5 w-3.5" />
                      Désactiver
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPending({
                        scheduleId: s.scheduleId,
                        action: "reset",
                        label: s.invoice ? `la facture ${s.invoice.number}` : "cette facture",
                      })
                    }
                  >
                    Réinitialiser le niveau
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>

      <Dialog
        open={!!pending}
        onOpenChange={(o) => (!o && !remediate.isPending ? setPending(null) : undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.action === "disable"
                ? "Désactiver la relance automatique"
                : "Réinitialiser le niveau de relance"}
            </DialogTitle>
            <DialogDescription>
              {pending?.action === "disable" ? (
                <>
                  Les relances automatiques de <strong>{pending?.label}</strong> seront
                  coupées (plus aucun envoi). Réversible en réinitialisant la relance.
                </>
              ) : (
                <>
                  La relance de <strong>{pending?.label}</strong> sera réactivée et
                  repartira au niveau 1 (1ère relance). L'historique d'escalade de cette
                  facture sera purgé. Action auditée et réversible via le journal d'audit.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPending(null)}
              disabled={remediate.isPending}
            >
              Annuler
            </Button>
            <Button
              variant={pending?.action === "reset" ? "destructive" : "default"}
              onClick={() =>
                pending &&
                remediate.mutate({ scheduleId: pending.scheduleId, action: pending.action })
              }
              disabled={remediate.isPending}
            >
              {remediate.isPending
                ? "Traitement..."
                : pending?.action === "disable"
                  ? "Désactiver"
                  : "Réinitialiser"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Migrate flat contacts (AOP-8547) ────────────────────────────────────────
function MigrateContactsButton({ companyId, companyName }: { companyId: string; companyName?: string }) {
  const { user } = useAuth();
  const canMigrate = user?.staffRole === "SUPER_ADMIN" || user?.staffRole === "ADMIN_OPS";

  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number; totalClients: number } | null>(null);

  const migrate = trpc.admin.contacts.migrateFlatContacts.useMutation({
    onSuccess: (res) => {
      setResult({ created: res.created, skipped: res.skipped, totalClients: res.totalClients });
      toast.success(`Migration terminée : ${res.created} créé(s), ${res.skipped} ignoré(s)`);
    },
    onError: (err) => toast.error(err.message),
  });

  if (!canMigrate) return null;

  const closeDialog = () => {
    if (migrate.isPending) return;
    setOpen(false);
    setResult(null);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => { setResult(null); setOpen(true); }}>
        <ArrowLeftRight className="h-4 w-4 mr-2" />
        Migrer contacts
      </Button>

      <Dialog open={open} onOpenChange={(o) => (!o ? closeDialog() : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Migrer les contacts</DialogTitle>
            <DialogDescription>
              Copie les contacts « plats » (champs <code>contact*</code> des clients) de{" "}
              <strong>{companyName ?? "cette entreprise"}</strong> vers le carnet de contacts
              (modèle Contact). Opération <strong>idempotente</strong> et non destructive — les
              champs d'origine sont conservés. Relancer ne crée pas de doublon.
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="rounded-md border bg-muted/40 p-4 text-sm space-y-1">
              <div className="font-medium text-success">Migration terminée</div>
              <div>Clients avec contact plat : <strong>{result.totalClients}</strong></div>
              <div>Contacts créés : <strong>{result.created}</strong></div>
              <div>Ignorés (déjà migrés) : <strong>{result.skipped}</strong></div>
            </div>
          ) : null}

          <DialogFooter>
            {result ? (
              <Button variant="outline" onClick={closeDialog}>Fermer</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeDialog} disabled={migrate.isPending}>
                  Annuler
                </Button>
                <Button onClick={() => migrate.mutate({ companyId })} disabled={migrate.isPending}>
                  {migrate.isPending ? "Migration en cours..." : "Lancer la migration"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: company, isLoading, isError } = trpc.admin.companies.get.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  const { data: usage, isLoading: usageLoading } = trpc.admin.companies.usageStats.useQuery(
    { companyId: id! },
    { enabled: !!id }
  );

  const { data: productStatus } = trpc.admin.companies.getProductStatus.useQuery(
    { companyId: id! } as any,
    { enabled: !!id }
  );

  const statusCfg = STATUS_CONFIG[(company as any)?.status] ?? { label: (company as any)?.status, variant: "secondary" };
  const lifecycleCfg = productStatus ? LIFECYCLE_CONFIG[(productStatus as any)?.lifecycle] : null;

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
          <AlertCircle className="h-10 w-10 text-destructive/40" />
          <p className="text-muted-foreground">Entreprise introuvable</p>
          <Button variant="outline" onClick={() => navigate("/tenants")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux tenants
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/tenants")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Tenants
          </Button>
          <div className="flex-1">
            {isLoading ? (
              <Skeleton className="h-8 w-64" />
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-semibold tracking-tight">{(company as any)?.name}</h1>
                <Badge variant={statusCfg.variant as any}>{statusCfg.label}</Badge>
                {(company as any)?.plan && (
                  <Badge variant="outline">
                    {PLAN_LABELS[(company as any).plan] ?? (company as any).plan}
                  </Badge>
                )}
                {lifecycleCfg && (
                  <Badge variant={lifecycleCfg.variant}>{lifecycleCfg.label}</Badge>
                )}
                {(productStatus as any)?.source && (
                  <span className="text-xs text-muted-foreground">
                    via {(productStatus as any).source === "travauxbtp" ? "TravauxBTP" : "Direct"}
                  </span>
                )}
              </div>
            )}
            {!isLoading && (
              <p className="text-muted-foreground mt-1 flex items-center gap-2">
                <Mail className="h-3.5 w-3.5" />
                {(company as any)?.email}
              </p>
            )}
          </div>
          {!isLoading && id && (
            <MigrateContactsButton companyId={id} companyName={(company as any)?.name} />
          )}
        </div>

        {/* Product Status */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" />
            Statut produits
          </h2>
          <ProductStatusSection companyId={id!} />
        </div>

        {/* Churn — bilan rentabilité moyen 90j (AOP-8511) */}
        {id && <ChurnProfitabilitySection companyId={id} />}

        {/* Adoption habilitations équipe (AOP-8579) */}
        {id && <QualificationsAdoptionSection companyId={id} />}

        {/* Suivi budgétaire chantiers + désactiver alertes (AOP-8587) */}
        {id && <BudgetTrackingSection companyId={id} />}

        {/* Factures récapitulatives RTC par chantier (AOP-8954 / AOP-8626) */}
        {id && <RecapInvoiceSection companyId={id} />}

        {/* Relances automatiques factures : statut + reset OPS (AOP-8956 / AOP-8632) */}
        {id && <ReminderScheduleSection companyId={id} />}

        {/* Profil */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4 text-primary" />
              Profil entreprise
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-start gap-2">
                  <Hash className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">SIRET</p>
                    <p className="text-sm font-medium">{(company as any)?.siret || "—"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Téléphone</p>
                    <p className="text-sm font-medium">{(company as any)?.phone || "—"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Adresse</p>
                    <p className="text-sm font-medium">{(company as any)?.address || "—"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Plan</p>
                    <p className="text-sm font-medium">
                      {PLAN_LABELS[(company as any)?.plan] ?? (company as any)?.plan ?? "—"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Inscrit le</p>
                    <p className="text-sm font-medium">{formatDate((company as any)?.createdAt)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Utilisateurs</p>
                    <p className="text-sm font-medium">{(company as any)?.User?.length ?? 0}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage SaaS — 30 jours */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            Usage SaaS — 30 derniers jours
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            {usageLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="pt-6">
                    <Skeleton className="h-8 w-12 mb-1" />
                    <Skeleton className="h-3 w-20" />
                  </CardContent>
                </Card>
              ))
            ) : (
              <>
                <StatCard label="Devis" value={usage?.nbDevis ?? 0} icon={FileText} />
                <StatCard label="Factures" value={usage?.nbFactures ?? 0} icon={Receipt} iconColor="text-success" />
                <StatCard label="Interventions" value={usage?.nbInterventions ?? 0} icon={Wrench} iconColor="text-warning" />
                <StatCard label="Sites" value={usage?.nbSites ?? 0} icon={MapPin} iconColor="text-primary" />
                <StatCard label="Projets AO" value={usage?.nbProjets ?? 0} icon={FolderOpen} />
                <StatCard
                  label="Mob. actifs"
                  value={usage?.nbConnexionsMobile ?? 0}
                  icon={Smartphone}
                  iconColor="text-primary"
                  sub="avec push token"
                />
              </>
            )}
          </div>
        </div>

        {/* Facturation + Projets */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Facturation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Euro className="h-4 w-4 text-success" />
                Facturation Constrigo
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-5 w-full" />)}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Total encaissé</span>
                    <span className="font-bold text-success">
                      {formatCurrency((company as any)?.billing?.totalRevenue ?? 0)}
                    </span>
                  </div>
                  <div className="border-t pt-3 space-y-2 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Factures service ({(company as any)?.billing?.serviceInvoiceCount ?? 0})</span>
                      <span>{formatCurrency((company as any)?.billing?.serviceRevenue ?? 0)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Assistances ({(company as any)?.billing?.assistanceCount ?? 0})</span>
                      <span>{formatCurrency((company as any)?.billing?.assistanceRevenue ?? 0)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Success fees ({(company as any)?.billing?.successFeeCount ?? 0})</span>
                      <span>{formatCurrency((company as any)?.billing?.successFeeRevenue ?? 0)}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Projets + Support */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Trophy className="h-4 w-4 text-warning" />
                  Projets AO
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-12 w-full" />
                ) : (
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-success">{(company as any)?.projectStats?.won ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Gagnés</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-destructive">{(company as any)?.projectStats?.lost ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Perdus</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{(company as any)?.projectStats?.winRate ?? 0}%</p>
                      <p className="text-xs text-muted-foreground">Taux de win</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{(company as any)?._count?.TenderMatch ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Matchs AO</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TicketCheck className="h-4 w-4 text-muted-foreground" />
                  Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <div className="flex items-center gap-2">
                    {(company as any)?.openTicketsCount > 0 ? (
                      <Badge variant="destructive">{(company as any).openTicketsCount} ticket(s) ouvert(s)</Badge>
                    ) : (
                      <Badge variant="secondary">Aucun ticket ouvert</Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Utilisateurs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-primary" />
              Utilisateurs du tenant
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-5 w-full" />)}
              </div>
            ) : (company as any)?.User?.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Aucun utilisateur</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 font-medium text-muted-foreground">Utilisateur</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Email</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Rôle</th>
                      <th className="text-left p-4 font-medium text-muted-foreground">Inscrit le</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(company as any)?.User?.map((u: any) => (
                      <tr key={u.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="p-4 font-medium">
                          {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                        </td>
                        <td className="p-4 text-muted-foreground">{u.email}</td>
                        <td className="p-4">
                          <Badge variant="secondary" className="text-xs">{u.role}</Badge>
                        </td>
                        <td className="p-4 text-muted-foreground">{formatDate(u.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
